import { convertToDrawing } from '@app/store/actions.js';
import { useMessages } from '@features/l10n/l10nInjector.js';
import { DetailsButton } from '@features/objects/components/DetailsButton.js';
import { useObjectActions } from '@features/objects/components/useObjectActions.js';
import { LongPressTooltip } from '@shared/components/LongPressTooltip.js';
import {
  Action,
  ResponsiveActions,
} from '@shared/components/ResponsiveActions.js';
import { Selection } from '@shared/components/Selection.js';
import {
  conversionSeed,
  featuresOf,
  useConvertPrompt,
} from '@shared/convertDialog.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import { convertibleLines } from '@shared/simplifyTolerance.js';
import { OsmFeatureIdSchema } from '@shared/types/featureId.js';
import type { ReactElement } from 'react';
import { Button } from 'react-bootstrap';
import { FaPencilAlt, FaSearch, FaThumbtack } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { searchKeepResult } from '../model/actions.js';
import {
  activeSearchResultKeptSelector,
  activeSearchResultSelector,
} from '../model/selectors.js';

type Props = {
  hidden?: boolean;
};

export function SearchSelection({ hidden }: Props): ReactElement | null {
  const m = useMessages();

  const dispatch = useDispatch();

  const askConversion = useConvertPrompt();

  const selectedResult = useAppSelector(activeSearchResultSelector);

  // False for every freshly picked result: it is on the map because it is being
  // looked at, and goes when that stops.
  const kept = useAppSelector(activeSearchResultKeptSelector);

  const { actions, onSelect } = useObjectActions({
    result: selectedResult ?? null,
  });

  return selectedResult &&
    !selectedResult.loading &&
    !window.fmEmbedded &&
    !hidden ? (
    // The pin and the delete are exclusive: a result being looked at can be
    // kept, a kept one taken off — the latter through `deletable`, before the ×.
    <Selection icon={<FaSearch />} label={m?.search.result} deletable={kept}>
      <DetailsButton />

      {!kept && (
        <LongPressTooltip breakpoint="sm" label={m?.search.keepOnMap}>
          {({ label, labelClassName, props }) => (
            <Button
              variant="secondary"
              onClick={() => {
                dispatch(searchKeepResult(selectedResult.id));
              }}
              {...props}
            >
              <FaThumbtack />
              <span className={labelClassName}> {label}</span>
            </Button>
          )}
        </LongPressTooltip>
      )}

      <ResponsiveActions
        gap={1}
        align="start"
        toggleLabel={m?.general.actions}
        onSelect={onSelect}
      >
        <Action
          icon={<FaPencilAlt />}
          label={m?.general.convertToDrawing}
          onClick={() => {
            const { geojson } = selectedResult;

            void askConversion({
              lines: geojson ? convertibleLines(geojson) : [],
              osm: OsmFeatureIdSchema.safeParse(selectedResult.id).success,
              ...conversionSeed(featuresOf(geojson)),
            }).then((choices) => {
              if (!choices) {
                return;
              }

              dispatch(
                convertToDrawing({
                  type: 'search-result',
                  tolerance: choices.tolerance,
                  carry: choices.carry,
                }),
              );
            });
          }}
          showFrom="never"
        />

        {actions}
      </ResponsiveActions>
    </Selection>
  ) : null;
}
